

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EntityEventChange, EventSummary } from '@/lib/types';
import type { FieldChange } from '@common/schemas/entities/event-change.schema';

import { useNovelRepository } from '@/hooks/useNovelRepository';
import { applyDeltas } from '@/lib/utils/apply-deltas';


interface EntityTimelineStepperProps {
  currentEntityData: Record<string, unknown> & { eventChanges?: EntityEventChange[] };
  onEntityDataChange: (data: Record<string, unknown>, isInitialState: boolean) => void;
}

export function EntityTimelineStepper({
  currentEntityData,
  onEntityDataChange,
}: EntityTimelineStepperProps) {
  const { t } = useTranslation();
  const { getSummaries } = useNovelRepository();
  const [activeStep, setActiveStep] = useState(0);

  const isLoading = false;

  // Use event summaries from context instead of fetching full documents
  const allEvents = useMemo(() => getSummaries<EventSummary>('events'), [getSummaries]);

  // Create event lookup map
  const eventsMap = useMemo(() => {
    const map = new Map<string, EventSummary>();
    allEvents?.forEach(event => map.set(event.id, event));
    return map;
  }, [allEvents]);

  // Embedded changes are already in currentEntityData
  const eventChanges = useMemo(() => {
    return (currentEntityData.eventChanges || [])
      .map(ec => ({
        ...ec,
        _resolvedOrder: eventsMap.get(ec.eventId)?.order ?? -1
      }))
      .sort((a, b) => a._resolvedOrder - b._resolvedOrder);
  }, [currentEntityData.eventChanges, eventsMap]);

  // Build timeline steps
  const steps = useMemo(() => {
    const initialStep = {
      label: t('timeline.entity.initial_state'),
      eventId: null,
      changes: [] as FieldChange[],
      date: undefined,
      description: undefined,
      orderStr: '0',
    };

    const eventSteps = (eventChanges || []).map(change => {
      const event = eventsMap.get(change.eventId);
      return {
        label: event?.name || t('timeline.entity.unknown_event'),
        date: event?.date,
        eventId: change.eventId,
        changes: change.changes || [],
        description: change.description,
        orderStr: event?.order !== undefined ? String(event.order + 1) : '?',
      };
    });

    return [initialStep, ...eventSteps];
  }, [eventChanges, eventsMap, t]);

  // Apply cumulative changes up to the selected step
  useEffect(() => {
    if (!currentEntityData) return;

    // Start with initial data
    // Start with initial data
    let stateAtStep = { ...currentEntityData };

    // Apply cumulative changes up to the selected step
    for (let i = 1; i <= activeStep && i < steps.length; i++) {
      const step = steps[i];
      if (step.changes && step.changes.length > 0) {
        stateAtStep = applyDeltas(stateAtStep, step.changes);
      }
    }

    // Pass state and whether we're at initial state (step 0)
    onEntityDataChange(stateAtStep, activeStep === 0);
  }, [activeStep, steps, currentEntityData, onEntityDataChange]);

  if (isLoading || !eventChanges || eventChanges.length === 0) {
    // Don't render if no changes exist
    return null;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('timeline.entity.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('timeline.entity.description')}
            </Typography>
          </Box>

          {/* Stepper */}
          <div style={{ overflowX: 'auto', overflowY: 'hidden', width: '100%', padding: '8px 0 16px' }}>
            <Stepper activeStep={activeStep} nonLinear alternativeLabel sx={{
              px: 1,
            }}>
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepButton onClick={() => setActiveStep(index)}>
                    <StepLabel
                      StepIconComponent={(props) => (
                        <Tooltip
                          title={
                            index > 0
                              ? t('timeline.entity.fields_modified', { count: step.changes?.length || 0 })
                              : t('timeline.entity.initial_state')
                          }
                          placement="top"
                          arrow
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: props.active || props.completed ? 'primary.main' : 'action.disabledBackground',
                              color: props.active || props.completed ? 'primary.contrastText' : 'text.disabled',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              zIndex: 1,
                            }}
                          >
                            {step.orderStr}
                          </Box>
                        </Tooltip>
                      )}
                    >
                      <Stack spacing={0.5} alignItems="center" minWidth={80}>
                        <Typography variant="body2" fontWeight={activeStep === index ? 600 : 400}>
                          {step.label}
                        </Typography>
                      </Stack>
                    </StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
}
